/**
 * Environment configuration loader
 *
 * URL/host values MUST come from environment files (.env.{development,stage,production})
 * or runtime injection via window.__ENV__ from the Kubernetes ConfigMap. Do NOT add
 * per-environment URL lookup dicts here — they were the root of repeated URL-mismatch
 * bugs when toggling local dev vs deployed dev vs stage vs prod.
 *
 * Resolution priority for every variable:
 *   window.__ENV__ (runtime K8s injection)  >  import.meta.env (build-time .env)  >  defaultConfig
 */

// The application version is a BUILD-TIME constant (vite.config.js `define`
// from package.json via scripts/build-info.mjs). A runtime VUE_APP_VERSION /
// VITE_APP_VERSION variable is deliberately ignored: the denv-new ConfigMap
// carried `VUE_APP_VERSION: 1.0.0` everywhere, so the live site reported 1.0.0
// while package.json was 2.0.2 (2026-10-04).
const BUILD_VERSION = typeof __APP_VERSION__ === 'string' && __APP_VERSION__ ? __APP_VERSION__ : '0.0.0'

// Default configuration values. Last-resort fallback ONLY.
// Set to PRODUCTION URLs so a missing env file fails safe (never points at localhost in prod).
const defaultConfig = {
  VUE_APP_API_BASE_URL: 'https://api.caucasus.systems',
  VUE_APP_ADMIN_PANEL_URL: 'https://admin.caucasus.systems',
  VUE_APP_CLUSTER_DASHBOARD_URL: 'https://caucasus.systems/cluster/management/dashboard',
  VUE_APP_WEBSITE_URL: 'https://caucasus.systems',
  // Canonical host for <link rel="canonical"> / og:url on the public marketing
  // pages (src/utils/page-meta.js). Brand domain by default; set per
  // environment if a non-production host should canonicalise to itself.
  VUE_APP_PUBLIC_SITE_URL: 'https://denvstack.com',
  VUE_APP_NAME: 'Caucasus Systems',
  VUE_APP_TITLE: 'AI-Native Development Platform',
  VUE_APP_VERSION: BUILD_VERSION,
  VUE_APP_API_TIMEOUT: 30000,
  VUE_APP_API_VERSION: '',
  VUE_APP_DEBUG: false,
  VUE_APP_ENABLE_RECAPTCHA: false,
  // Legacy VITE_ variables for backward compatibility
  VITE_API_BASE_URL: 'https://api.caucasus.systems',
  VITE_ADMIN_PANEL_URL: 'https://admin.caucasus.systems',
  VITE_CLUSTER_DASHBOARD_URL: 'https://caucasus.systems/cluster/management/dashboard',
  VITE_WEBSITE_URL: 'https://caucasus.systems',
  VITE_APP_NAME: 'Caucasus Systems',
  VITE_APP_TITLE: 'AI-Native Development Platform',
  VITE_APP_VERSION: BUILD_VERSION,
  VITE_API_TIMEOUT: 30000,
  VITE_API_VERSION: '',
  VITE_DEBUG: false,
  VITE_ENABLE_RECAPTCHA: false
}

/**
 * Get environment configuration.
 * Priority: window.__ENV__ (runtime K8s vars) > import.meta.env (build-time) > defaultConfig
 */
export function getEnvConfig() {
  // Get runtime environment from injected script (Kubernetes ConfigMap)
  const runtimeEnv = (typeof window !== 'undefined' && window.__ENV__) || {}

  const nodeEnv = runtimeEnv.NODE_ENV || runtimeEnv.VITE_NODE_ENV ||
                  import.meta.env.MODE || import.meta.env.NODE_ENV || 'production'

  const config = {
    NODE_ENV: nodeEnv,
    VITE_NODE_ENV: nodeEnv,
    VUE_APP_NODE_ENV: nodeEnv,

    // ----- API Configuration (VUE_APP_ prefix) -----
    VUE_APP_API_BASE_URL:
      runtimeEnv.VUE_APP_API_BASE_URL ||
      import.meta.env.VUE_APP_API_BASE_URL ||
      defaultConfig.VUE_APP_API_BASE_URL,

    VUE_APP_API_VERSION:
      (runtimeEnv.VUE_APP_API_VERSION ||
       import.meta.env.VUE_APP_API_VERSION ||
       defaultConfig.VUE_APP_API_VERSION || '').replace(/['"]/g, '').trim(),

    VUE_APP_API_TIMEOUT:
      Number(runtimeEnv.VUE_APP_API_TIMEOUT ||
             import.meta.env.VUE_APP_API_TIMEOUT) ||
      defaultConfig.VUE_APP_API_TIMEOUT,

    // ----- App Metadata -----
    VUE_APP_NAME:
      runtimeEnv.VUE_APP_NAME ||
      import.meta.env.VUE_APP_NAME ||
      defaultConfig.VUE_APP_NAME,

    VUE_APP_TITLE:
      runtimeEnv.VUE_APP_TITLE ||
      import.meta.env.VUE_APP_TITLE ||
      defaultConfig.VUE_APP_TITLE,

    // Build-time only (package.json); runtime/env values are ignored on purpose.
    VUE_APP_VERSION: BUILD_VERSION,

    // ----- Feature Flags -----
    VUE_APP_DEBUG:
      runtimeEnv.VUE_APP_DEBUG === 'true' || runtimeEnv.VUE_APP_DEBUG === true ||
      import.meta.env.VUE_APP_DEBUG === 'true' || import.meta.env.VUE_APP_DEBUG === true ||
      nodeEnv === 'development',

    VUE_APP_ENABLE_RECAPTCHA:
      runtimeEnv.VUE_APP_ENABLE_RECAPTCHA === 'true' || runtimeEnv.VUE_APP_ENABLE_RECAPTCHA === true ||
      import.meta.env.VUE_APP_ENABLE_RECAPTCHA === 'true' || import.meta.env.VUE_APP_ENABLE_RECAPTCHA === true ||
      false,

    // ----- Platform Environment Registry (P1) dark flag -----
    // Gates the customer Environments surface: the /admin/environments
    // platform↔org toggle, the instance-first create wizard, the env workspace
    // detail tabs, and the /my/certificates surface (+ its nav entries).
    // DEFAULT OFF — ships dark so the live site is unchanged until enabled.
    // Resolution order matches every other flag here:
    //   window.__ENV__ (K8s ConfigMap) > import.meta.env (.env file) > false.
    VUE_APP_ENVIRONMENT_REGISTRY_ENABLED:
      runtimeEnv.VUE_APP_ENVIRONMENT_REGISTRY_ENABLED === 'true' || runtimeEnv.VUE_APP_ENVIRONMENT_REGISTRY_ENABLED === true ||
      import.meta.env.VUE_APP_ENVIRONMENT_REGISTRY_ENABLED === 'true' || import.meta.env.VUE_APP_ENVIRONMENT_REGISTRY_ENABLED === true ||
      false,

    // Is backup-api deployed behind this face? DEFAULT ON. A face that serves
    // the website without backup-api (site-b dev.denvstack.com, 2026-10-22)
    // sets it to false so the portal never calls /api/backup/* there — those
    // calls fell through to the website's own nginx and logged 404s on every
    // Dashboard load. Only an explicit false (string or boolean) disables it.
    VUE_APP_BACKUPS_ENABLED: !(
      runtimeEnv.VUE_APP_BACKUPS_ENABLED === 'false' || runtimeEnv.VUE_APP_BACKUPS_ENABLED === false ||
      (runtimeEnv.VUE_APP_BACKUPS_ENABLED === undefined &&
        (import.meta.env.VUE_APP_BACKUPS_ENABLED === 'false' || import.meta.env.VUE_APP_BACKUPS_ENABLED === false))
    ),

    // ----- Admin Panel URL (SSO target) -----
    VUE_APP_ADMIN_PANEL_URL:
      runtimeEnv.VUE_APP_ADMIN_PANEL_URL ||
      import.meta.env.VUE_APP_ADMIN_PANEL_URL ||
      defaultConfig.VUE_APP_ADMIN_PANEL_URL,

    // ----- Cluster Dashboard URL (super-admin SSO target into cluster-api) -----
    VUE_APP_CLUSTER_DASHBOARD_URL:
      runtimeEnv.VUE_APP_CLUSTER_DASHBOARD_URL ||
      import.meta.env.VUE_APP_CLUSTER_DASHBOARD_URL ||
      defaultConfig.VUE_APP_CLUSTER_DASHBOARD_URL,

    // ----- Website URL (frontend host) -----
    VUE_APP_WEBSITE_URL:
      runtimeEnv.VUE_APP_WEBSITE_URL ||
      import.meta.env.VUE_APP_WEBSITE_URL ||
      defaultConfig.VUE_APP_WEBSITE_URL,

    // ----- Public marketing site (canonical / og:url host) -----
    VUE_APP_PUBLIC_SITE_URL:
      runtimeEnv.VUE_APP_PUBLIC_SITE_URL ||
      import.meta.env.VUE_APP_PUBLIC_SITE_URL ||
      defaultConfig.VUE_APP_PUBLIC_SITE_URL,

    // ----- Legacy VITE_ mirrors (back-compat) -----
    VITE_API_BASE_URL:
      runtimeEnv.VUE_APP_API_BASE_URL || runtimeEnv.VITE_API_BASE_URL ||
      import.meta.env.VUE_APP_API_BASE_URL || import.meta.env.VITE_API_BASE_URL ||
      defaultConfig.VITE_API_BASE_URL,

    VITE_API_VERSION:
      (runtimeEnv.VUE_APP_API_VERSION || runtimeEnv.VITE_API_VERSION ||
       import.meta.env.VUE_APP_API_VERSION || import.meta.env.VITE_API_VERSION ||
       defaultConfig.VITE_API_VERSION || '').replace(/['"]/g, '').trim(),

    VITE_API_TIMEOUT:
      Number(runtimeEnv.VUE_APP_API_TIMEOUT || runtimeEnv.VITE_API_TIMEOUT ||
             import.meta.env.VUE_APP_API_TIMEOUT || import.meta.env.VITE_API_TIMEOUT) ||
      defaultConfig.VITE_API_TIMEOUT,

    VITE_APP_NAME:
      runtimeEnv.VUE_APP_NAME || runtimeEnv.VITE_APP_NAME ||
      import.meta.env.VUE_APP_NAME || import.meta.env.VITE_APP_NAME ||
      defaultConfig.VITE_APP_NAME,

    VITE_APP_TITLE:
      runtimeEnv.VUE_APP_TITLE || runtimeEnv.VITE_APP_TITLE ||
      import.meta.env.VUE_APP_TITLE || import.meta.env.VITE_APP_TITLE ||
      defaultConfig.VITE_APP_TITLE,

    VITE_APP_VERSION: BUILD_VERSION,

    VITE_DEBUG:
      runtimeEnv.VUE_APP_DEBUG === 'true' || runtimeEnv.VUE_APP_DEBUG === true ||
      runtimeEnv.VITE_DEBUG === 'true' || runtimeEnv.VITE_DEBUG === true ||
      import.meta.env.VUE_APP_DEBUG === 'true' || import.meta.env.VUE_APP_DEBUG === true ||
      import.meta.env.VITE_DEBUG === 'true' || import.meta.env.VITE_DEBUG === true ||
      nodeEnv === 'development',

    VITE_ENABLE_RECAPTCHA:
      runtimeEnv.VUE_APP_ENABLE_RECAPTCHA === 'true' || runtimeEnv.VUE_APP_ENABLE_RECAPTCHA === true ||
      runtimeEnv.VITE_ENABLE_RECAPTCHA === 'true' || runtimeEnv.VITE_ENABLE_RECAPTCHA === true ||
      import.meta.env.VUE_APP_ENABLE_RECAPTCHA === 'true' || import.meta.env.VUE_APP_ENABLE_RECAPTCHA === true ||
      import.meta.env.VITE_ENABLE_RECAPTCHA === 'true' || import.meta.env.VITE_ENABLE_RECAPTCHA === true ||
      false,

    VITE_ADMIN_PANEL_URL:
      runtimeEnv.VUE_APP_ADMIN_PANEL_URL || runtimeEnv.VITE_ADMIN_PANEL_URL ||
      import.meta.env.VUE_APP_ADMIN_PANEL_URL || import.meta.env.VITE_ADMIN_PANEL_URL ||
      defaultConfig.VITE_ADMIN_PANEL_URL,

    VITE_CLUSTER_DASHBOARD_URL:
      runtimeEnv.VUE_APP_CLUSTER_DASHBOARD_URL || runtimeEnv.VITE_CLUSTER_DASHBOARD_URL ||
      import.meta.env.VUE_APP_CLUSTER_DASHBOARD_URL || import.meta.env.VITE_CLUSTER_DASHBOARD_URL ||
      defaultConfig.VITE_CLUSTER_DASHBOARD_URL,

    VITE_WEBSITE_URL:
      runtimeEnv.VUE_APP_WEBSITE_URL || runtimeEnv.VITE_WEBSITE_URL ||
      import.meta.env.VUE_APP_WEBSITE_URL || import.meta.env.VITE_WEBSITE_URL ||
      defaultConfig.VITE_WEBSITE_URL
  }

  // Add computed values - clean up API version and build full URL
  const cleanApiVersion = (version) => {
    if (!version) return ''
    const cleaned = String(version).replace(/['"]/g, '').trim()
    // Ignore common incorrect values
    if (cleaned === 'v1' || cleaned === '/v1' || cleaned === 'api/v1') {
      console.warn('Ignoring incorrect API_VERSION value:', cleaned)
      return ''
    }
    return cleaned
  }

  const vueApiVersion = cleanApiVersion(config.VUE_APP_API_VERSION)
  const viteApiVersion = cleanApiVersion(config.VITE_API_VERSION)

  config.VUE_APP_API_FULL_URL = vueApiVersion
    ? `${config.VUE_APP_API_BASE_URL}/api/${vueApiVersion}`
    : `${config.VUE_APP_API_BASE_URL}/api`
  config.VITE_API_FULL_URL = viteApiVersion
    ? `${config.VITE_API_BASE_URL}/api/${viteApiVersion}`
    : `${config.VITE_API_BASE_URL}/api`

  return config
}

/**
 * Get a specific environment variable with fallback
 * Priority: window.__ENV__ (runtime K8s vars) > import.meta.env (build-time) > defaultValue
 */
export function getEnv(key, defaultValue = '') {
  const runtimeEnv = (typeof window !== 'undefined' && window.__ENV__) || {}
  const config = getEnvConfig()
  return config[key] || runtimeEnv[key] || import.meta.env[key] || defaultValue
}

/**
 * Check if running in specific environment
 */
export function isEnv(env) {
  const runtimeEnv = (typeof window !== 'undefined' && window.__ENV__) || {}
  const nodeEnv = runtimeEnv.NODE_ENV || runtimeEnv.VITE_NODE_ENV ||
                  import.meta.env.MODE || import.meta.env.NODE_ENV || 'production'
  return nodeEnv === env
}

export const isDevelopment = () => isEnv('development')
export const isStage = () => isEnv('stage')
export const isProduction = () => isEnv('production') || isEnv('main')

/**
 * Platform Environment Registry (P1) dark flag.
 * TRUE only when VUE_APP_ENVIRONMENT_REGISTRY_ENABLED is explicitly enabled
 * (runtime K8s var or build-time .env). Default false → the new Environments
 * surface, create wizard and /my/certificates entries stay hidden.
 */
export const isEnvironmentRegistryEnabled = () =>
  getEnvConfig().VUE_APP_ENVIRONMENT_REGISTRY_ENABLED === true

/**
 * Backups surface flag. FALSE only when VUE_APP_BACKUPS_ENABLED is explicitly
 * false — the face has no backup-api, so /my/backups shows a "not on this
 * environment" note and the Dashboard skips the backup calls.
 */
export const isBackupsEnabled = () =>
  getEnvConfig().VUE_APP_BACKUPS_ENABLED !== false

// Export the configuration
export const envConfig = getEnvConfig()

// Log configuration in development
if (isDevelopment()) {
  console.log('Environment Configuration:', envConfig)
}
